import type { Workflow } from '@/components/ApprovalStageWizard/types';
import type { UpdateWorkflowRequest } from '@/types/workflow';
import { mapApproverTypeToBackend, mapRoleTypeToBackend } from './workflowMappers';

// Helper to build backend update request from a workflow object
export const buildUpdateRequest = (workflow: Workflow): UpdateWorkflowRequest => {
  return {
    name: workflow.name,
    description: workflow.description,
    isActive: workflow.status === 'active',
    isDefault: workflow.isDefault ?? false,
    stages: workflow.stages.map((stage) => {
      const stageDataToUse = stage.stageData;

      // Map approver from stage data
      const approverType = stageDataToUse?.approver
        ? mapApproverTypeToBackend(stageDataToUse.approver.approverType)
        : 'manager' as const;

      const approverObj: {
        approverType: 'specific' | 'role' | 'manager' | 'department_head' | 'custom';
        approverId?: string;
        approverRole?: string;
        approverOrder: number;
      } = {
        approverType,
        approverOrder: 1,
      };

      if (stageDataToUse?.approver) {
        if (stageDataToUse.approver.specificUserId) {
          approverObj.approverId = String(stageDataToUse.approver.specificUserId);
        }
        if (stageDataToUse.approver.roleType) {
          approverObj.approverRole = mapRoleTypeToBackend(stageDataToUse.approver.roleType);
        }
      }

      // Use undefined for temporary stage IDs so backend generates proper GUIDs
      const backendStageId = (stage.id.startsWith('temp-') || stage.id.startsWith('placeholder-')) ? undefined : stage.id;

      // Build advanced config JSON from stageData
      const advancedConfig = stageDataToUse ? {
        smartRules: {
          rules: stageDataToUse.smartRules.rules,
          onApproved: stageDataToUse.smartRules.onApproved,
          onRejected: stageDataToUse.smartRules.onRejected,
          timeout: stageDataToUse.smartRules.timeout,
        },
        notifications: stageDataToUse.notifications,
      } : undefined;

      return {
        id: backendStageId,
        name: stage.name,
        description: stage.description,
        stageOrder: stage.order,
        approvalType: 'any' as const,
        requiredApprovals: 1,
        allowApproverEdit: stageDataToUse?.allowApproverEdit ?? false,
        advancedConfigJson: advancedConfig ? JSON.stringify(advancedConfig) : undefined,
        approvers: [{
          ...approverObj,
          cannotApproveOwn: stageDataToUse?.approver?.cannotApproveOwn ?? true,
          ruleConfigJson: stageDataToUse?.approver?.ruleDetermined
            ? JSON.stringify(stageDataToUse.approver.ruleDetermined)
            : undefined,
        }],
      };
    }),
  };
};
